import { useEffect } from "react";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const abs = (url) => {
  if (!url) return "";
  return url.startsWith("http") ? url : `${ORIGIN}${url}`;
};

// Client-side SEO manager: sets document title, meta/OG/Twitter tags,
// canonical link and JSON-LD structured data. Cleans up on unmount.
export default function Seo({ title, description, keywords, canonical, ogImage, ogTitle, ogDescription, ogType = "website", jsonLd = [], noindex = false }) {
  const jsonLdKey = JSON.stringify(jsonLd);
  useEffect(() => {
    const created = [];
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", content);
    };
    if (title) document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);
    setMeta("property", "og:title", ogTitle || title);
    setMeta("property", "og:description", ogDescription || description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", abs(canonical));
    setMeta("property", "og:image", abs(ogImage));
    if (ogImage) {
      setMeta("property", "og:image:width", "1200");
      setMeta("property", "og:image:height", "630");
    }
    setMeta("property", "og:site_name", "eTrialTrack");
    setMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", ogTitle || title);
    setMeta("name", "twitter:description", ogDescription || description);
    setMeta("name", "twitter:image", abs(ogImage));
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    if (!noindex) setMeta("name", "googlebot", "index, follow, max-image-preview:large");
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
        created.push(link);
      }
      link.setAttribute("href", abs(canonical));
      // single-language site: self-referencing hreflang (en + x-default)
      ["en", "x-default"].forEach((lang) => {
        let alt = document.head.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
        if (!alt) {
          alt = document.createElement("link");
          alt.setAttribute("rel", "alternate");
          alt.setAttribute("hreflang", lang);
          document.head.appendChild(alt);
          created.push(alt);
        }
        alt.setAttribute("href", abs(canonical));
      });
    }
    const scripts = (jsonLd || []).filter(Boolean).map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    return () => {
      created.forEach((el) => el.remove());
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonical, ogImage, ogTitle, ogDescription, ogType, jsonLdKey, noindex]);
  return null;
}
